// Edit a task group (S112): the creation wizard's source, secrets and review steps, prefilled
// from the group. Its task type, workflow and source kind stay (shown, not offered). Saving is
// one `PUT /api/task-groups/{id}`; the API keeps everything the body names and nothing else.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "rizzui";

import { accessQuery } from "../auth/access";
import { useT } from "../i18n";
import { detailCrumbs } from "../layout/nav";
import { toast } from "../layout/toast";
import { credentialsQuery } from "../secrets/api";
import { updateTaskGroup } from "../tasks/create/api";
import { type Credential, type Draft, prefill, reachable, STEPS } from "../tasks/create/draft";
import { EDIT_STEPS, updateBody } from "../tasks/create/edit";
import { Navigator } from "../tasks/create/frame/Navigator";
import { StepHeader } from "../tasks/create/frame/StepHeader";
import { StepRail } from "../tasks/create/frame/StepRail";
import { stepProblem } from "../tasks/create/problems";
import { AccessStep } from "../tasks/create/steps/AccessStep";
import { ReviewStep } from "../tasks/create/steps/ReviewStep";
import { SourceStep } from "../tasks/create/steps/SourceStep";
import { useAddSecret } from "../tasks/create/useAddSecret";
import { useEditGroup } from "../tasks/create/useEditGroup";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { ErrorState, Loading } from "../ui/states";

const EMPTY: Credential[] = [];

export default function EditTaskGroupPage() {
  const { tasks } = useT();
  const t = tasks.create;
  const e = t.edit;
  const { groupId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = useQuery(accessQuery()).data?.personal_org_id;
  const credentials = useQuery(credentialsQuery(orgId)).data ?? EMPTY;
  const loaded = useEditGroup(groupId, e.workflowFallback);
  const [edited, setEdited] = useState<Draft>();
  const [step, setStep] = useState(0);
  const base = edited ?? loaded.draft;
  const draft = base && step >= EDIT_STEPS.indexOf("access") ? prefill(base, credentials) : base;
  const change = (patch: Partial<Draft>) => draft && setEdited({ ...draft, ...patch });
  const adder = useAddSecret(orgId, (c, target) => (target === "source" ? change({ credentialId: c.id, bindings: {} }) : draft && change({ bindings: { ...draft.bindings, [target]: c.id } })));
  const back = `/task-groups/${groupId}`;
  const save = useMutation({
    mutationFn: () => updateTaskGroup(groupId, updateBody(draft!)),
    onSuccess: async (group) => {
      toast(e.saved(group.name));
      await queryClient.invalidateQueries({ queryKey: ["task-groups"] });
      void navigate(back);
    },
  });

  const crumbs = detailCrumbs("taskGroups", "/task-groups", { name: loaded.group?.name ?? e.title });
  if (loaded.error) return <ErrorState title={e.loadFailed} message={loaded.error.message} />;
  if (!draft || !loaded.group) return <Loading />;
  const key = EDIT_STEPS[step]!;
  const last = step === EDIT_STEPS.length - 1;
  // The type and workflow steps are always done here, so the full wizard's reach, less them.
  const canReach = Math.min(EDIT_STEPS.length - 1, Math.max(0, reachable(draft) - STEPS.indexOf("source")));
  const go = (to: number) => setStep(Math.max(0, Math.min(to, canReach)));
  const body = {
    source: <SourceStep draft={draft} onChange={change} credentials={credentials} onAdd={adder.open && ((kind) => adder.open?.(kind, "source"))} fixedSource />,
    access: <AccessStep draft={draft} onChange={change} credentials={credentials} onAdd={adder.open} />,
    review: <ReviewStep draft={draft} onChange={change} credentials={credentials} onEdit={(i) => go(EDIT_STEPS.indexOf(STEPS[i] as (typeof EDIT_STEPS)[number]))} editable={EDIT_STEPS} />,
  }[key];
  const copy = t.steps[key];

  return (
    <>
      <PageHeader title={e.title} breadcrumb={crumbs} description={e.subtitle(loaded.group.name)}>
        <Button variant="outline" onClick={() => void navigate(back)}>
          {e.cancel}
        </Button>
      </PageHeader>
      <div className="grid items-start gap-6 lg:grid-cols-[15rem_minmax(0,1fr)] xl:gap-10">
        <StepRail label={t.progress} steps={EDIT_STEPS.map((s) => ({ key: s, label: t.steps[s].label }))} current={step} reachable={canReach} onJump={go} />
        <section aria-label={copy.title} className={cn(cardClass, "min-w-0 max-w-4xl rounded-[20px] p-5 md:p-8 lg:p-10")}>
          <StepHeader title={copy.title} description={{ source: e.sourceDescription, access: copy.description, review: e.reviewDescription }[key]} step={step} total={EDIT_STEPS.length} counter={t.stepOf(step + 1, EDIT_STEPS.length)} />
          {body}
          {save.isError ? <ErrorState className="mt-6" title={e.saveFailed} message={save.error.message} /> : null}
          <Navigator
            first={step === 0}
            last={last}
            blocked={stepProblem(draft, key)}
            busy={save.isPending}
            onBack={() => go(step - 1)}
            onNext={() => (last ? save.mutate() : go(step + 1))}
            finishLabel={e.save}
          />
        </section>
      </div>
      {adder.drawer}
    </>
  );
}
